import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { localStorage } from '#app/services/storage.js';
import { type Variant } from './types.js';
import { getRoute } from '@contra/routes';
import { useCallback, useState } from 'react';

const BASE_KEY = 'CONTRA_LOCAL_PROFILE_MAGIC_BANNER_CTA_OPT_OUT_USER';

type useProfileMagicBannerCTAProps = {
  displayUsername: string;
  variant: Variant;
  visitorCanEdit: boolean;
};

type useProfileMagicBannerCTAReturn = {
  CTAHref: string;
  handleCTAClick: () => void;
  handleCTADismiss: () => void;
  shouldShowCTA: boolean;
};

/**
 * @package
 */
export const useProfileMagicBannerCTA = ({
  displayUsername,
  variant,
  visitorCanEdit,
}: useProfileMagicBannerCTAProps): useProfileMagicBannerCTAReturn => {
  const { track } = useAnalytics();

  const currentUser = useCurrentUser();

  const storageKey = `${BASE_KEY}_${variant.toUpperCase()}_${
    currentUser?.id?.toUpperCase() ?? 'NA'
  }` as const;

  const [storageKeyValue, setStorageKeyValue] = useState(
    localStorage.getItem(storageKey),
  );
  const hasDismissedCTA = storageKeyValue === 'true';

  const hasUsedProfileMagic = useHasUserInterfaceFlags(
    'HIDE_FTUE_CONTRA_PROFILE_MAGIC',
  );

  const shouldShowCTA =
    visitorCanEdit && !hasUsedProfileMagic && !hasDismissedCTA;

  const handleCTADismiss = useCallback(() => {
    setStorageKeyValue('true');
    localStorage.setItem(storageKey, 'true');

    track('button', {
      action: 'tapped',
      button_name: 'growth_profile_magic_banner_cta_dismiss',
      section: variant,
    });
  }, [storageKey, track, variant]);

  const handleCTAClick = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'growth_profile_magic_banner_cta_primary',
      section: variant,
    });
  }, [track, variant]);

  const CTAHref =
    variant === 'service'
      ? getRoute('/@username/services', {
          args: {
            username: displayUsername,
          },
          search: {
            profileMagic: true,
          },
        })
      : getRoute('/@username/projects', {
          args: {
            username: displayUsername,
          },
          search: {
            profileMagic: true,
          },
        });

  return {
    CTAHref,
    handleCTAClick,
    handleCTADismiss,
    shouldShowCTA,
  };
};
